import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
	CircularLoader,
	CustomButton,
	CustomIconButton,
	DocumentAutocompleteSearch,
	GenericDialog,
	GenericDialogContent,
	GenericDialogFooter,
	GenericDialogHeader,
	icon,
	IconComponent,
} from "../../../../../../common/components";
import { translate } from "../../../../../../common/providers";
import styles from "./DocumentCenterVersionDialog.module.css";
import { useApi } from "../../../../../../common/hooks";
import { ProjectDocumentService, ProjectDocumentsService, SmartViewService } from "../../../../../../api";
import { debounce, isNonEmptyArray, updateTreeBranch } from "../../../../../../common/utils";
import VersioningDocumentTree from "./VersioningDocumentTree";

const handleFormatRecursiveDirData = (folders) => {
	if (!isNonEmptyArray(folders)) {
		return null;
	}
	let formatedArray = [];
	folders.forEach((folder) => {
		let formatedElement = {
			id: folder.id,
			label: folder.label,
			expanded: true,
			type: "FOLDER",
			documents: folder.documents?.map((document) => ({ ...document, type: "FILE" })),
		};
		if (isNonEmptyArray(folder?.children)) {
			formatedElement = {
				...formatedElement,
				expanded: true,
				documents: [...(folder?.documents || []), ...handleFormatRecursiveDirData(folder.children)],
			};
		}
		formatedArray = [...formatedArray, formatedElement];
	});
	return formatedArray;
};
const handleFormatDirData = (unformatedData) => {
	let documentList = [];
	let folderList = [];
	if (isNonEmptyArray(unformatedData.documentsInRoot)) {
		documentList = unformatedData.documentsInRoot.map((folder) => ({ ...folder, type: "FILE" }));
	}
	if (isNonEmptyArray(unformatedData.folders)) {
		folderList = handleFormatRecursiveDirData(unformatedData.folders);
	}
	return [...folderList, ...documentList];
};

const DocumentCenterVersionDialog = ({
	disabled,
	isValid,
	open,
	selectedDocId,
	selectedDocumentLabel,
	onCloseDialog,
	onChange,
	onValidateNewDocument,
	onResetValidation,
}) => {
	const [docs, setDocs] = useState([]);
	const [loadingDocuments, setLoadingDocuments] = useState(false);
	const [searchDocument, setSearchDocument] = useState([]);
	const [searchDocumentInputValue, setSearchDocumentInputValue] = useState("");
	const [searchDebouncedDocument, setSearchDebouncedDocument] = useState("");
	const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
	const [selectedDocument, setSelectedDocument] = useState(null);
	const [isOpenWarning, setIsOpenWarning] = useState(false);
	const projectId = useSelector(({ context }) => context.project?.id);
	const { call: searchDocs } = useApi(ProjectDocumentService.search);
	const { call: getDirectory } = useApi(ProjectDocumentsService.getDirectory);
	const { call: searchInDirectoryByDocumentName } = useApi(SmartViewService.searchInDirectoryByDocumentName);

	useEffect(() => {
		if (isValid === false) {
			setIsOpenWarning(true);
		} else {
			setIsOpenWarning(false);
		}
	}, [isValid]);
	useEffect(() => {
		if (
			searchDebouncedDocument &&
			searchDebouncedDocument === searchDocumentInputValue &&
			searchDebouncedDocument.length > 1
		) {
			searchDocs({ projectId }, { name: searchDebouncedDocument })
				.then((data) => {
					setSearchDocument(data);
				})
				.catch(console.error)
				.finally(() => setIsLoadingDocuments(false));
		}
	}, [projectId, searchDocs, searchDebouncedDocument, searchDocumentInputValue]);

	const debouncedSearch = debounce((name) => {
		setSearchDebouncedDocument(name);
	}, 500);
	const handleResetDocument = () => {
		setSelectedDocument(null);
		onResetValidation();
	};
	const handleSearchDocument = (name) => {
		setSearchDocumentInputValue(name);
		handleResetDocument();
		if (!name) {
			setSearchDocument([]);
			setIsLoadingDocuments(false);
			return;
		}
		setIsLoadingDocuments(true);
		debouncedSearch(name);
	};
	const handleDocumentSelection = ({ documentName }) => {
		const result = documentName.replace(/\.pdf$/, "");
		setSelectedDocument(result);
	};
	const getTree = useCallback(() => {
		getDirectory({ projectId, directoryId: "root" })
			.then((data) => {
				setDocs(data);
			})
			.catch(console.error)
			.finally(() => setLoadingDocuments(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getDirectory, projectId]);
	const getTreeWithSearch = useCallback(() => {
		searchInDirectoryByDocumentName({ projectId }, { search: selectedDocument })
			.then((data) => {
				setDocs(handleFormatDirData(data));
			})
			.catch(console.error)
			.finally(() => setLoadingDocuments(false));

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectId, selectedDocument, searchInDirectoryByDocumentName]);
	useEffect(() => {
		setLoadingDocuments(true);
		if (selectedDocument) {
			getTreeWithSearch();
		} else {
			getTree();
		}
	}, [selectedDocument, getTree, getTreeWithSearch]);
	const isFolder = (el) => el.type === "FOLDER";
	const setTreeValue = (directory, elements) => {
		setDocs((prev) =>
			updateTreeBranch({
				tree: prev,
				additionalConditions: isFolder,
				idToReplace: directory.id,
				identifier: "id",
				newBranch: {
					...directory,
					documents: elements,
					expanded: !directory.expanded,
				},
				iterativeBranchName: "documents",
			})
		);
	};
	const handleToggleExpand = (directory, onCallBack) => {
		if (!isNonEmptyArray(directory.documents)) {
			getDirectory({ projectId, directoryId: directory.id })
				.then((data) => {
					setTreeValue(directory, data);
				})
				.catch((err) => console.error(err))
				.finally(() => onCallBack(false));
		} else {
			setDocs((prev) =>
				updateTreeBranch({
					tree: prev,
					additionalConditions: isFolder,
					idToReplace: directory.id,
					identifier: "id",
					newBranch: {
						...directory,
						expanded: directory.filtered !== undefined ? false : !directory.expanded,
						filtered: directory.prevFilteredState,
						prevFilteredState: directory.filtered,
					},
					iterativeBranchName: "documents",
				})
			);
			onCallBack(false);
		}
	};
	const handleCloseDialog = () => {
		handleResetDocument();
		onCloseDialog();
	};
	const handleCloseWarning = () => {
		setIsOpenWarning(false);
	};
	return (
		<GenericDialog
			fullWidth
			className={styles.versioningDialog}
			maxWidth="sm"
			open={open}
			onClose={handleCloseDialog}
		>
			<GenericDialogHeader className={styles.versioningHeader}>
				<CustomIconButton
					icon={icon.faTimes}
					iconClassName={styles.versioningHeader__icon}
					iconColor="var(--grey-500)"
					onClick={handleCloseDialog}
				/>
				<span className={styles.versioningHeader__title}>
					{translate("common:document.versioning.dialoge-box.title")}
				</span>
			</GenericDialogHeader>
			<GenericDialogContent className={styles.versioningContent}>
				<div className={styles.docInfo}>
					<span className={styles.docInfo_status}>
						{translate("document-center.side-panel.versioning.dialoge-box.doc-status")}
					</span>
					<span className={styles.docInfo_name}>{selectedDocumentLabel}</span>
				</div>
				<div className={styles.docList}>
					<div className={styles.docList_search}>
						<DocumentAutocompleteSearch
							className={styles.autoComplete}
							documents={searchDocument?.contents || []}
							emptyStateLabel={translate("common:document-autocomplete-search.empty-state")}
							error={false}
							helperText={translate("document-center.search.helper")}
							inputValue={searchDocumentInputValue}
							isLoading={isLoadingDocuments}
							label={translate("document-center.search.title")}
							onReset={handleResetDocument}
							onSearch={handleSearchDocument}
							onSelect={handleDocumentSelection}
						/>
					</div>
					{(loadingDocuments && (
						<div className={styles.loaderContainer}>
							<CircularLoader size={50} />
						</div>
					)) || (
						<div className={styles.docList_scrollable}>
							<VersioningDocumentTree
								disabled={disabled}
								documents={docs || []}
								hasSearch={selectedDocument}
								mode="folder"
								selectedDocId={selectedDocId}
								onCheck={onChange}
								onToggleExpand={handleToggleExpand}
							/>
						</div>
					)}
				</div>
				{!isValid && (
					<div className={`${styles.docWarning} ${!isOpenWarning ? styles.noWarning : ""}`}>
						<div className={styles.docWarning_icon}>
							<IconComponent color="var(--red-500)" icon={icon.faExclamationTriangle} size="sm" />
						</div>
						<div className={styles.docWarning_left}>
							<span className={styles.docWarning_title}>
								{translate(
									"document-center.side-panel.versioning.dialoge-box.invalid-doc.warning.title"
								)}
							</span>
							<span className={styles.docWarning_desc}>
								{translate(
									"document-center.side-panel.versioning.dialoge-box.invalid-doc.warning.desc"
								)}
							</span>
						</div>
						<div>
							<CustomIconButton
								icon={icon.faTimes}
								iconClassName={styles.docWarning_icon}
								iconColor="var(--grey-500)"
								size="sm"
								onClick={handleCloseWarning}
							/>
						</div>
					</div>
				)}
			</GenericDialogContent>
			<GenericDialogFooter className={styles.versioningFooter}>
				<CustomButton color="primary" variant="outlined" onClick={handleCloseDialog}>
					{translate("common:btn.cancel")}
				</CustomButton>
				<CustomButton color="primary" disabled={!isValid} variant="contained" onClick={onValidateNewDocument}>
					{translate("common:btn.validate")}
				</CustomButton>
			</GenericDialogFooter>
		</GenericDialog>
	);
};
export default DocumentCenterVersionDialog;
